import { BOARD_SIZE } from "../../game/constants";
import type { Rect, UiRects } from "../../game/input";
import type { TutorialState } from "../../game/tutorial";
import {
  mFont,
  posKey,
  roundedRectPath,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface TutorialOverlayDeps {
  ctx: CanvasRenderingContext2D;
  box: number;
  boardY: number;
  tps: number;
  bps: number;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawTutorialOverlayScreen(
  deps: TutorialOverlayDeps,
  tutorial: TutorialState,
  uiRects: UiRects,
): void {
  const { ctx, box, boardY, tps, bps } = deps;
  const pulse = 0.5 + 0.5 * Math.sin(tutorial.elapsed * 4);
  const hasHighlightCells = tutorial.highlightCells.length > 0;
  const hasHighlightRects = tutorial.highlightRects.length > 0;
  const boardGridSize = Math.max(1, Math.floor(uiRects.boardGridSize ?? BOARD_SIZE));

  // ── Board dim overlay ─────────────────────────────────────────
  if (hasHighlightCells) {
    // Dim board cells except highlighted ones
    const highlightSet = new Set(tutorial.highlightCells.map((c) => posKey(c.row, c.col)));
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    for (let row = 0; row < boardGridSize; row++) {
      for (let col = 0; col < boardGridSize; col++) {
        if (highlightSet.has(posKey(row, col))) continue;
        const x = box + col * tps;
        const y = boardY + row * tps;
        ctx.fillRect(x, y, tps, tps);
      }
    }
  } else if (hasHighlightRects) {
    // For non-board steps, dim the entire board area
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.fillRect(box, boardY, bps, bps);
  }

  // ── Highlight board cells ─────────────────────────────────────
  for (const cell of tutorial.highlightCells) {
    const x = box + cell.col * tps;
    const y = boardY + cell.row * tps;
    ctx.strokeStyle = `rgba(255,220,80,${0.5 + pulse * 0.5})`;
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 1, y + 1, tps - 2, tps - 2);
    ctx.lineWidth = 1;
  }

  // ── Swap arrow ────────────────────────────────────────────────
  if (tutorial.highlightSwap != null) {
    const from = tutorial.highlightSwap.from;
    const to = tutorial.highlightSwap.to;
    const fx = box + from.col * tps + tps / 2;
    const fy = boardY + from.row * tps + tps / 2;
    const tx = box + to.col * tps + tps / 2;
    const ty = boardY + to.row * tps + tps / 2;
    const bobOffset = Math.sin(tutorial.elapsed * 3) * 3;
    ctx.strokeStyle = `rgba(255,220,80,${0.6 + pulse * 0.4})`;
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(fx + bobOffset, fy);
    ctx.lineTo(tx + bobOffset, ty);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    const arrowSize = 5;
    const angle = Math.atan2(ty - fy, tx - fx);
    ctx.fillStyle = `rgba(255,220,80,${0.6 + pulse * 0.4})`;
    ctx.beginPath();
    ctx.moveTo(tx + bobOffset, ty);
    ctx.lineTo(tx + bobOffset - arrowSize * Math.cos(angle - 0.5), ty - arrowSize * Math.sin(angle - 0.5));
    ctx.lineTo(tx + bobOffset - arrowSize * Math.cos(angle + 0.5), ty - arrowSize * Math.sin(angle + 0.5));
    ctx.closePath();
    ctx.fill();
  }

  // ── Highlight UI rects (golden pulse border) ──────────────────
  for (const rectName of tutorial.highlightRects) {
    const rect = (uiRects as Record<string, Rect | undefined>)[rectName];
    if (rect == null) continue;
    const pad = 3;
    ctx.strokeStyle = `rgba(255,220,80,${0.5 + pulse * 0.5})`;
    ctx.lineWidth = 2;
    ctx.strokeRect(rect.x - pad, rect.y - pad, rect.w + pad * 2, rect.h + pad * 2);
    // Outer glow
    ctx.strokeStyle = `rgba(255,220,80,${0.15 + pulse * 0.15})`;
    ctx.lineWidth = 4;
    ctx.strokeRect(rect.x - pad - 2, rect.y - pad - 2, rect.w + pad * 2 + 4, rect.h + pad * 2 + 4);
    ctx.lineWidth = 1;
  }

  // ── Text panel — positioned near the focal point ──────────────
  const panelH = 48;
  const panelW = bps * 0.75;
  const panelGap = 8;
  let focalCenterY: number;
  let focalCenterX: number;

  if (hasHighlightCells) {
    // Average position of highlighted board cells
    const avgRow = tutorial.highlightCells.reduce((s, c) => s + c.row, 0) / tutorial.highlightCells.length;
    const avgCol = tutorial.highlightCells.reduce((s, c) => s + c.col, 0) / tutorial.highlightCells.length;
    focalCenterY = boardY + (avgRow + 0.5) * tps;
    focalCenterX = box + (avgCol + 0.5) * tps;
  } else if (hasHighlightRects) {
    // Average center of highlighted UI rects
    let sumX = 0, sumY = 0, count = 0;
    for (const name of tutorial.highlightRects) {
      const r = (uiRects as Record<string, Rect | undefined>)[name];
      if (r == null) continue;
      sumX += r.x + r.w / 2;
      sumY += r.y + r.h / 2;
      count++;
    }
    focalCenterX = count > 0 ? sumX / count : box + bps / 2;
    focalCenterY = count > 0 ? sumY / count : boardY + bps / 2;
  } else {
    focalCenterX = box + bps / 2;
    focalCenterY = boardY + bps / 2;
  }

  // Place panel above focal point; if that would go above the board, place below
  let panelY = focalCenterY - panelH - panelGap - tps / 2;
  if (panelY < boardY + 4) {
    panelY = focalCenterY + tps / 2 + panelGap;
  }
  // Clamp within board area
  panelY = Math.max(boardY + 4, Math.min(panelY, boardY + bps - panelH - 4));

  // Center horizontally near focal X, but clamp within board
  let panelX = focalCenterX - panelW / 2;
  panelX = Math.max(box + 4, Math.min(panelX, box + bps - panelW - 4));

  roundedRectPath(ctx, panelX, panelY, panelW, panelH, 8);
  ctx.fillStyle = "rgba(10,15,31,0.92)";
  ctx.fill();
  roundedRectPath(ctx, panelX, panelY, panelW, panelH, 8);
  ctx.strokeStyle = "#ffd84a";
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.lineWidth = 1;

  ctx.fillStyle = "#ffeeb0";
  ctx.font = mFont("bold", 8);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const textX = panelX + panelW / 2;
  for (let i = 0; i < tutorial.textLines.length; i++) {
    ctx.fillText(tutorial.textLines[i], textX, panelY + 16 + i * 16);
  }
}
